/*
  Pinch-to-zoom / drag-to-pan / double-tap-to-zoom for the images inside the
  product media lightbox (<product-modal>). Scoped entirely to that modal's
  images — the site keeps `maximum-scale=1` (native pinch-zoom disabled)
  everywhere else, so this reimplements the gesture by hand with Pointer
  Events rather than relying on the browser's own pinch-zoom.
*/
(function () {
  var MAX_SCALE = 4;
  var DOUBLE_TAP_SCALE = 2.5;
  var DOUBLE_TAP_MS = 300;

  function ZoomController(modal) {
    this.modal = modal;
    this.pointers = new Map();
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.startDistance = 0;
    this.startScale = 1;
    this.startX = 0;
    this.startY = 0;
    this.panPointerId = null;
    this.panStartX = 0;
    this.panStartY = 0;
    this.lastTapTime = 0;
    this.img = null;

    modal.addEventListener('pointerdown', this.onPointerDown.bind(this));
    modal.addEventListener('pointermove', this.onPointerMove.bind(this));
    modal.addEventListener('pointerup', this.onPointerUp.bind(this));
    modal.addEventListener('pointercancel', this.onPointerUp.bind(this));
    modal.addEventListener('dblclick', this.onDoubleClick.bind(this));
  }

  ZoomController.prototype.activeImage = function () {
    var active = this.modal.querySelector('.product-media-modal__content > .active');
    if (!active) return null;
    return active.matches('img') ? active : active.querySelector('img');
  };

  ZoomController.prototype.reset = function () {
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.pointers.clear();
    this.panPointerId = null;
    if (this.img) this.applyTransform();
    this.img = null;
  };

  ZoomController.prototype.applyTransform = function () {
    if (!this.img) return;
    this.img.style.transform = 'translate(' + this.x + 'px, ' + this.y + 'px) scale(' + this.scale + ')';
    this.img.style.transition = 'none';
    this.img.classList.toggle('is-zoomed', this.scale > 1);
  };

  ZoomController.prototype.clamp = function () {
    this.scale = Math.min(Math.max(this.scale, 1), MAX_SCALE);
    if (this.scale === 1) {
      this.x = 0;
      this.y = 0;
      return;
    }
    if (!this.img) return;
    var rect = this.img.getBoundingClientRect();
    var maxX = Math.max(0, (rect.width * this.scale - rect.width) / 2);
    var maxY = Math.max(0, (rect.height * this.scale - rect.height) / 2);
    this.x = Math.min(Math.max(this.x, -maxX), maxX);
    this.y = Math.min(Math.max(this.y, -maxY), maxY);
  };

  ZoomController.prototype.onPointerDown = function (event) {
    var img = this.activeImage();
    if (!img || !img.contains(event.target) && event.target !== img) return;
    this.img = img;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.pointers.size === 2) {
      var pts = Array.from(this.pointers.values());
      this.startDistance = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      this.startScale = this.scale;
      this.panPointerId = null;
    } else if (this.pointers.size === 1 && this.scale > 1) {
      this.panPointerId = event.pointerId;
      this.panStartX = event.clientX - this.x;
      this.panStartY = event.clientY - this.y;
    }
  };

  ZoomController.prototype.onPointerMove = function (event) {
    if (!this.img || !this.pointers.has(event.pointerId)) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.pointers.size === 2) {
      var pts = Array.from(this.pointers.values());
      var distance = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (this.startDistance > 0) {
        this.scale = this.startScale * (distance / this.startDistance);
        this.clamp();
        this.applyTransform();
      }
      event.preventDefault();
    } else if (this.panPointerId === event.pointerId && this.scale > 1) {
      this.x = event.clientX - this.panStartX;
      this.y = event.clientY - this.panStartY;
      this.clamp();
      this.applyTransform();
      event.preventDefault();
    }
  };

  ZoomController.prototype.onPointerUp = function (event) {
    if (!this.pointers.has(event.pointerId)) return;
    var wasSingleTap = this.pointers.size === 1 && this.scale === 1;
    this.pointers.delete(event.pointerId);
    if (this.panPointerId === event.pointerId) this.panPointerId = null;

    if (this.pointers.size < 2) this.startDistance = 0;

    if (wasSingleTap && event.pointerType === 'touch') {
      var now = Date.now();
      if (now - this.lastTapTime < DOUBLE_TAP_MS) {
        this.toggleZoom(event.clientX, event.clientY);
        this.lastTapTime = 0;
      } else {
        this.lastTapTime = now;
      }
    }
  };

  ZoomController.prototype.onDoubleClick = function (event) {
    if (event.pointerType === 'touch') return;
    var img = this.activeImage();
    if (!img || (!img.contains(event.target) && event.target !== img)) return;
    this.img = img;
    this.toggleZoom(event.clientX, event.clientY);
  };

  ZoomController.prototype.toggleZoom = function (clientX, clientY) {
    if (!this.img) return;
    if (this.scale > 1) {
      this.scale = 1;
      this.x = 0;
      this.y = 0;
    } else {
      var rect = this.img.getBoundingClientRect();
      var offsetX = clientX - (rect.left + rect.width / 2);
      var offsetY = clientY - (rect.top + rect.height / 2);
      this.scale = DOUBLE_TAP_SCALE;
      this.x = -offsetX * (this.scale - 1);
      this.y = -offsetY * (this.scale - 1);
      this.clamp();
    }
    this.img.style.transition = 'transform 0.2s ease';
    this.applyTransform();
  };

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('product-modal').forEach(function (modal) {
      var controller = new ZoomController(modal);

      var originalShow = modal.show.bind(modal);
      modal.show = function (opener) {
        controller.reset();
        originalShow(opener);
      };

      var originalHide = modal.hide.bind(modal);
      modal.hide = function () {
        controller.reset();
        originalHide();
      };

      var originalShowActiveMedia = modal.showActiveMedia.bind(modal);
      modal.showActiveMedia = function () {
        controller.reset();
        originalShowActiveMedia();
      };
    });
  });
})();
